/**
 * Patient-facing After-Visit Summary view.
 *
 * Token-gated (no auth required); the link is emailed to the patient when
 * a provider publishes the AVS from the visit cockpit. Mirrors the
 * existing PrescriptionLink pattern at src/pages/PrescriptionLink.tsx.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FileCheck2, Loader2, Printer, CheckCircle2, AlertTriangle, Calendar,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { avsService, AfterVisitSummary } from '@/services/avsService';
import { toast } from '@/hooks/use-toast';

const PatientAvs: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [acknowledging, setAcknowledging] = useState(false);

  const { data: avs, isLoading, isError, refetch } = useQuery({
    queryKey: ['patient-avs', token],
    queryFn: () => avsService.getAvsByToken(token!),
    enabled: !!token,
  });

  const acknowledge = async () => {
    if (!token) return;
    setAcknowledging(true);
    try {
      await avsService.acknowledgeAvs(token);
      await refetch();
      toast({ title: 'Receipt acknowledged', variant: 'success' });
    } catch (err: any) {
      toast({ title: 'Failed to acknowledge', description: err?.message, variant: 'destructive' });
    } finally {
      setAcknowledging(false);
    }
  };

  const handlePrint = () => window.print();

  const visitDate = useMemo(() => {
    if (!avs?.visit_date) return null;
    return new Date(avs.visit_date).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  }, [avs?.visit_date]);

  if (!token) {
    return <Empty message='Missing or invalid link.' />;
  }

  if (isLoading) {
    return (
      <div className='flex min-h-screen items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-brand-teal' />
      </div>
    );
  }

  if (isError || !avs) {
    return <Empty message='This after-visit summary is unavailable. The link may have expired.' />;
  }

  return (
    <div className='min-h-screen bg-slate-50 print:bg-white'>
      <div className='mx-auto max-w-3xl p-6'>
        <Card>
          <CardHeader className='space-y-3'>
            <div className='flex items-center justify-between'>
              <CardTitle className='flex items-center gap-2'>
                <FileCheck2 className='h-5 w-5 text-brand-teal' />
                After-Visit Summary
              </CardTitle>
              <div className='flex gap-2 print:hidden'>
                <Button variant='outline' size='sm' onClick={handlePrint}>
                  <Printer className='mr-1 h-3 w-3' /> Print
                </Button>
              </div>
            </div>
            {visitDate && (
              <div className='flex items-center gap-2 text-sm text-slate-500'>
                <Calendar className='h-4 w-4' />
                Visit on {visitDate}
              </div>
            )}
          </CardHeader>

          <CardContent className='space-y-5'>
            {avs.chief_complaint && (
              <Section title='Reason for visit'>
                <p className='text-sm'>{avs.chief_complaint}</p>
              </Section>
            )}

            {avs.visit_summary && (
              <Section title='Summary'>
                <p className='text-sm whitespace-pre-wrap'>{avs.visit_summary}</p>
              </Section>
            )}

            {avs.diagnoses && avs.diagnoses.length > 0 && (
              <Section title='Diagnoses'>
                <ul className='space-y-1 text-sm'>
                  {avs.diagnoses.map((d, i) => (
                    <li key={i}>{d.display}{d.code ? <Badge variant='outline' className='ml-2 text-[10px]'>{d.code}</Badge> : null}</li>
                  ))}
                </ul>
              </Section>
            )}

            {avs.medication_changes && avs.medication_changes.length > 0 && (
              <Section title='Medication changes'>
                <ul className='space-y-2 text-sm'>
                  {avs.medication_changes.map((m, i) => (
                    <li key={i} className='flex items-start gap-2'>
                      <Badge variant='outline' className='shrink-0 capitalize'>{m.action}</Badge>
                      <div>
                        <div className='font-medium'>{m.medication_display}</div>
                        {m.dose_text && <div className='text-xs text-slate-500'>{m.dose_text}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {avs.orders && avs.orders.length > 0 && (
              <Section title='Orders placed'>
                <ul className='space-y-2 text-sm'>
                  {avs.orders.map((o, i) => (
                    <li key={i}>
                      <div className='flex items-center gap-2'>
                        <Badge variant='outline' className='capitalize'>{o.category}</Badge>
                        <span className='font-medium'>{o.display}</span>
                      </div>
                      {o.patient_instructions && (
                        <div className='ml-1 mt-1 text-xs text-slate-600'>{o.patient_instructions}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {avs.patient_instructions && (
              <Section title='Your instructions'>
                <p className='text-sm whitespace-pre-wrap'>{avs.patient_instructions}</p>
              </Section>
            )}

            {avs.follow_up_date && (
              <Section title='Follow up'>
                <p className='text-sm'>
                  Please return on{' '}
                  <span className='font-medium'>
                    {new Date(avs.follow_up_date).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric',
                    })}
                  </span>
                  {avs.follow_up_notes && ` — ${avs.follow_up_notes}`}.
                </p>
              </Section>
            )}

            {!avs.acknowledged_at ? (
              <div className='rounded-md border border-emerald-200 bg-emerald-50 p-4 print:hidden'>
                <p className='text-sm text-emerald-900 mb-3'>
                  Please confirm that you've reviewed this summary.
                </p>
                <Button
                  onClick={acknowledge}
                  disabled={acknowledging}
                  className='bg-brand-teal hover:bg-brand-teal/90'
                >
                  {acknowledging
                    ? <><Loader2 className='mr-2 h-4 w-4 animate-spin' /> Saving…</>
                    : <><CheckCircle2 className='mr-2 h-4 w-4' /> Acknowledge receipt</>}
                </Button>
              </div>
            ) : (
              <div className='rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 print:hidden'>
                <CheckCircle2 className='mr-1 inline h-4 w-4' />
                Acknowledged on {new Date(avs.acknowledged_at).toLocaleString('en-US')}.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h3 className='mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500'>{title}</h3>
    {children}
  </div>
);

const Empty: React.FC<{ message: string }> = ({ message }) => (
  <div className='flex min-h-screen items-center justify-center bg-slate-50 p-6'>
    <Card>
      <CardContent className='flex items-center gap-3 py-8'>
        <AlertTriangle className='h-5 w-5 text-amber-600' />
        <p className='text-sm text-slate-700'>{message}</p>
      </CardContent>
    </Card>
  </div>
);

export default PatientAvs;
